import React, { useEffect, useRef } from "react";

const TOOLBAR_STYLE = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  marginBottom: "14px",
  padding: "12px 14px",
  borderRadius: "14px",
  border: "1px solid rgba(201,168,76,0.25)",
  background: "rgba(255,255,255,0.04)",
};

const SELECT_ALL_ROW_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
};

const CHECKBOX_STYLE = {
  width: "18px",
  height: "18px",
  accentColor: "#c9a84c",
  cursor: "pointer",
};

const ACTIONS_ROW_STYLE = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
};

const ACTION_BTN_BASE = {
  borderRadius: "999px",
  padding: "8px 14px",
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0.04em",
  cursor: "pointer",
  border: "1px solid rgba(201,168,76,0.45)",
  background: "rgba(201,168,76,0.12)",
  color: "#c9a84c",
};

const DESTRUCTIVE_ACTION_BTN = {
  ...ACTION_BTN_BASE,
  border: "1px solid rgba(224,107,90,0.45)",
  background: "rgba(224,107,90,0.12)",
  color: "#e06b5a",
};

function BatchActionButton({ action, selectedCount, disabled, onClick }) {
  const label =
    typeof action.label === "function"
      ? action.label(selectedCount)
      : action.label;
  const needsSelection = action.requiresSelection !== false;

  return (
    <button
      type="button"
      style={action.destructive ? DESTRUCTIVE_ACTION_BTN : ACTION_BTN_BASE}
      disabled={disabled || (needsSelection && selectedCount === 0)}
      onClick={() => onClick(action.id)}
    >
      {label}
    </button>
  );
}

/**
 * Select-all control and batch action buttons for hub list screens.
 *
 * @param {{
 *   allSelected: boolean,
 *   someSelected: boolean,
 *   onToggleAll: () => void,
 *   selectAllLabel?: string,
 *   selectedCount: number,
 *   actions?: Array<{ id: string, label: string|((count: number) => string), destructive?: boolean, requiresSelection?: boolean, alwaysVisible?: boolean }>,
 *   onAction: (actionId: string) => void,
 *   disabled?: boolean,
 * }} props
 */
export function BatchSelectionToolbar({
  allSelected,
  someSelected,
  onToggleAll,
  selectAllLabel = "Select All",
  selectedCount,
  actions = [],
  onAction,
  disabled = false,
}) {
  const selectAllRef = useRef(null);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  return (
    <div style={TOOLBAR_STYLE}>
      <label style={SELECT_ALL_ROW_STYLE}>
        <input
          ref={selectAllRef}
          type="checkbox"
          checked={allSelected}
          disabled={disabled}
          onChange={onToggleAll}
          style={CHECKBOX_STYLE}
        />
        <span>{selectAllLabel}</span>
      </label>

      {actions.some((action) => action.alwaysVisible || someSelected) && (
        <div style={ACTIONS_ROW_STYLE}>
          {actions
            .filter((action) => action.alwaysVisible || someSelected)
            .map((action) => (
              <BatchActionButton
                key={action.id}
                action={action}
                selectedCount={selectedCount}
                disabled={disabled}
                onClick={onAction}
              />
            ))}
        </div>
      )}
    </div>
  );
}

export const BATCH_SELECTION_CHECKBOX_STYLE = CHECKBOX_STYLE;
